import { omit } from 'lodash';
import { clearTextFromHTMLTagsAndBreak } from 'src/helpers/seo';

import { ILocationV2 } from '@sravni/types/lib/locations';
import { IOrganization } from '@sravni/types/lib/organizations';

import { SravniNewsSharedEnumsMaterialType } from '../__generated__/news/Api';
import { getAuthorAvatarUrl, getMaterialLink } from '../helpers/news';
import {
  IAdditionalSchemaInfo,
  IAuthorObjectScheme,
  IImageObjectScheme,
  IMaterialSchema,
  IMaterialsPageSchema,
  IPublisherObjectScheme,
  ISingleMaterialPageSchema,
} from '../interfaces/metadata';
import { IHighlightedMaterial, IMaterial, TAuthorShort } from '../interfaces/news';

export const getLocationSeoParams = (location: ILocationV2) => {
  return {
    id: location.garId,
    name: location.name,
    nameLocative: location.nameLocative,
    route: location.route,
    alias: location.alias,
    latitude: location.latitude,
    longitude: location.longitude,
  };
};

export const getOrganizationSeoParams = (organization: IOrganization) => {
  return {
    id: organization.oldId,
    name: organization.name.short,
    alias: organization.alias,
    type: organization.type,
  };
};

const getMaterialUrl = (
  material,
  additionalInfo: { rubric?: string; rubricAlias?: string; rubricType?: string },
): string => {
  const { externalLink } = material;
  if (!!externalLink) return externalLink;

  const rubric = additionalInfo?.rubric;
  const rubricAlias = additionalInfo?.rubricAlias;
  const rubricType = additionalInfo?.rubricType;

  const link = getMaterialLink(material, {
    ...((rubric || rubricAlias) && { rubricAlias: rubric || rubricAlias } && { rubricType: rubricType || rubricType }),
  });
  return `${process.env.WEB_PATH}${link.as}`;
};

export const makeMaterialListPageSchema = (
  list: IHighlightedMaterial[],
  additionalInfo?: IAdditionalSchemaInfo,
): IMaterialsPageSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    mainEntity: {
      '@type': 'ItemList',
      url: additionalInfo?.url || '',
      name: additionalInfo?.title || '',
      numberOfItems: list.length,
      itemListElement: list.map((listItem) => {
        return makeJsonLDSchemaFromMaterial(listItem);
      }),
    },
  };
};

export const makeSingleMaterialPageSchema = (
  material: IMaterial,
  author: TAuthorShort,
  additionalInfo: IAdditionalSchemaInfo,
): ISingleMaterialPageSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    mainEntity: makeJsonLDSchemaFromMaterial(material, author, additionalInfo),
  };
};

export const makeJsonLDSchemaFromMaterial = (
  material: IMaterial,
  author?: TAuthorShort,
  additionalInfo?: IAdditionalSchemaInfo,
): IMaterialSchema => {
  const isNews = material.type === SravniNewsSharedEnumsMaterialType.News;
  const title = additionalInfo?.title || clearTextFromHTMLTagsAndBreak(material.title);
  const description = additionalInfo?.description || (material.lead && clearTextFromHTMLTagsAndBreak(material.lead));

  const publisher = {
    '@type': 'Organization',
    name: 'sravni.ru',
    telephone: '+78007073902',
    logo: {
      '@type': 'ImageObject',
      image: 'https://s91588.cdn.ngenix.net/shared/static/images/design-system/sravni-logo-sign.png',
      url: 'https://s91588.cdn.ngenix.net/shared/static/images/design-system/sravni-logo-sign.png',
    },
    foundingDate: '20090905T000000+0400', // '09.05.2009',
    sameAs: 'https://ru.wikipedia.org/wiki/%D0%A1%D1%80%D0%B0%D0%B2%D0%BD%D0%B8.%D1%80%D1%83',
    address: {
      '@type': 'PostalAddress',
      name: '109544, г. Москва, бульвар Энтузиастов, дом 2, 26 этаж.',
    },
  } as IPublisherObjectScheme;

  const authorImg: IImageObjectScheme = author
    ? {
        '@type': 'ImageObject',
        image: getAuthorAvatarUrl(author.id),
        url: getAuthorAvatarUrl(author.id),
      }
    : undefined;

  let authorScheme: IMaterialSchema['author'] =
    author || material.authorName
      ? ({
          '@type': 'Person',
          name: author?.displayName || material.authorName,
          jobTitle: author?.title || undefined,
          image: authorImg,
          ...(author && { url: `https://www.sravni.ru/u/${author?.id}` }),
          ...(omit(author, 'instagram', 'facebook', 'telegram', 'title', 'displayName', 'isExpert', 'rating', 'id') ||
            {}),
        } as IAuthorObjectScheme)
      : ({} as IAuthorObjectScheme);

  if (additionalInfo?.rubricAlias === 'putevoditel') {
    authorScheme = publisher;
  }

  return {
    '@type': isNews ? 'NewsArticle' : 'Article',
    url: getMaterialUrl(material, {
      rubric: additionalInfo?.rubric,
      rubricAlias: additionalInfo?.rubricAlias,
      rubricType: additionalInfo?.url,
    }),
    headline: title,
    description: description,
    thumbnailUrl: material.picture?.url,
    dateCreated: material.createDate,
    datePublished: material.publishDate,
    dateModified: material.lastChangeDate,
    publisher,
    keywords: material.tags.join(', '),
    inLanguage: 'ru-RU',
    commentCount: material.commentsCount,
    author: authorScheme,
    ...(material.picture && {
      image: material.picture?.url,
    }),
  };
};
